import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function DashboardButton({ DashboardMenu, item }: any) {
    const { t } = useTranslation();
    const isRTL = t("dir") === "rtl";

    // Find the menu item
    const menuItem = DashboardMenu.find((items: any) => items.name === item);
    
    if (!menuItem || !menuItem.sub || menuItem.sub.length === 0) {
        return null;
    }

    const getGradientColor = (color: string) => {
        const colorMap: { [key: string]: string } = {
            'blue': 'from-blue-500 to-blue-600',
            '#CCD5AE': 'from-green-400 to-green-500',
            '#E0E5B6': 'from-yellow-400 to-yellow-500',
            '#E8B86D': 'from-orange-400 to-orange-500',
            '#CCD5EE': 'from-indigo-400 to-indigo-500',
            '#FAEDCE': 'from-amber-300 to-amber-400',
            '#CEDF9F': 'from-lime-400 to-lime-500',
            'purple': 'from-purple-500 to-purple-600',
            'pink': 'from-pink-500 to-pink-600',
            'indigo': 'from-indigo-500 to-indigo-600',
            'cyan': 'from-cyan-500 to-cyan-600',
            'orange': 'from-orange-500 to-orange-600',
        };
        return colorMap[color] || 'from-gray-500 to-gray-600';
    };

    return (
        <div className="space-y-4" dir={t("dir")}>
            {/* Header - Smaller */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <div 
                    className={`relative w-full h-20 rounded-xl shadow-lg overflow-hidden bg-gradient-to-br ${getGradientColor(menuItem.color || 'blue')}`}
                    style={{ 
                        backgroundImage: menuItem.color ? undefined : 'url(/walls.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="relative z-10 h-full flex items-center p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                                <img
                                    src={menuItem.icon}
                                    alt=""
                                    className="w-6 h-6 filter brightness-0 invert"
                                />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">
                                    {t(menuItem.name)}
                                </h2>
                                <p className="text-white/80 text-xs">
                                    {t('Select an option below')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Submenu Grid - Smaller */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {menuItem.sub.map((p: any, index: number) => (
                    <motion.div
                        key={p.url || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ y: -3 }}
                    >
                        <Link
                            to={p.url}
                            className="block h-full group"
                        >
                            <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group-hover:border-blue-200 h-full flex flex-col">
                                {/* Icon Section - Smaller */}
                                <div className={`bg-gradient-to-br ${getGradientColor(menuItem.color || 'blue')} p-4 relative overflow-hidden`}>
                                    <div className="absolute inset-0 bg-black/10"></div>
                                    <div className="relative z-10 flex items-center justify-center">
                                        <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                                            {p.icon ? (
                                                <img
                                                    src={p.icon}
                                                    alt=""
                                                    className="w-7 h-7 filter brightness-0 invert"
                                                />
                                            ) : (
                                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Content Section - Smaller */}
                                <div className="p-3 flex-1 flex items-center justify-center min-h-[50px]">
                                    <h3 className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors text-center">
                                        {t(p.name)}
                                    </h3>
                                </div>

                                {/* Hover Arrow Indicator - Smaller */}
                                <div className="px-3 pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="flex items-center justify-center gap-1 text-blue-600 text-xs font-medium">
                                        <span>{t('Open')}</span>
                                        <svg 
                                            className={`w-3 h-3 ${isRTL ? 'rotate-180' : ''}`}
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
